import {
  SignedFile,
  SignedUrlResponse,
} from "@coworker/apprestructured/src/task-management/services/uploads.service";
import {
  CreateUpload,
  NewOrExistingUpload,
  SignedUpload,
} from "@coworker/apprestructured/src/task-management/types/Upload";
import {
  ExistingFile,
  NewOrExistingFile,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";

export type UploaderResult = {
  // Images - true if images can be uploaded, meaning:
  //    - all images have been compressed
  //    - signed urls has been fetched for all images
  // Files - true if all files have signed urls
  ready: boolean;

  // Upload the images/files to the bucket
  upload: () => Promise<void>;

  // CreateUpload objects for all new images/files
  // as well as references to existing uploads
  newOrExistingUploads: NewOrExistingUpload[];

  // CreateUpload objects for all new images/files
  createUploads: CreateUpload[];
};

export function convertToSignedFiles(
  files: File[],
  signedUrlResponses: SignedUrlResponse[]
): SignedFile[] {
  return files.map((file) => {
    const signedUrlResponse = signedUrlResponses.find(
      ({ originalFileName }) => originalFileName === file.name
    )!;
    return { file, signedUrl: signedUrlResponse.signedUrl };
  });
}

export function calcHasSignedUrlsForAllFiles(
  files: File[],
  signedUrlResponses: SignedUrlResponse[]
) {
  return (
    files.length === signedUrlResponses.length &&
    files.every((file) =>
      signedUrlResponses.some(
        (response) => response.originalFileName === file.name
      )
    )
  );
}

/**
 * Convert a list of files into a list of CreateUpload.
 * Each File#name must have a corresponding WriteSignedUrlResponse#originalFileName.
 *
 * @param newOrExistingFiles the new or existing images to convert
 * @param signedUrlResponses signed urls for all files
 */
export function newOrExistingFilesToNewOrExistingUploads(
  newOrExistingFiles: NewOrExistingFile[],
  signedUrlResponses: SignedUrlResponse[]
): NewOrExistingUpload[] {
  return newOrExistingFiles.map((newOrExistingFile) => {
    switch (newOrExistingFile.type) {
      case "new": {
        const bucket_id =
          signedUrlResponses.find(
            ({ originalFileName }) =>
              originalFileName === newOrExistingFile.file.name
          )?.fileName || null;

        if (bucket_id === null) {
          throw new Error(
            `No signed url exists for file '${newOrExistingFile.file.name}'`
          );
        }

        return {
          type: "new",
          createUpload: {
            name: newOrExistingFile.file.name,
            size: newOrExistingFile.file.size,
            bucket_id,
          },
        };
      }
      case "existing": {
        return {
          type: "existing",
          uploadId: newOrExistingFile.file.id,
        };
      }
    }
  });
}

/**
 * Note that existing uploads in the given array will be filtered out.
 */
export function newOrExistingUploadsToCreateUploads(
  newOrExistingUploads: NewOrExistingUpload[]
): CreateUpload[] {
  const arr: CreateUpload[] = [];
  for (let i = 0; i < newOrExistingUploads.length; i++) {
    const newOrExistingUpload = newOrExistingUploads[i]!;
    if (newOrExistingUpload.type === "new") {
      arr.push(newOrExistingUpload.createUpload);
    }
  }
  return arr;
}

export function signedUploadsToExistingFiles(
  signedUploads: SignedUpload[]
): ExistingFile[] {
  return signedUploads.map(({ id, name, size, signed_url }) => {
    return {
      type: "existing",
      file: { id, name, size: parseInt(size), url: signed_url },
    };
  });
}
